/**
 * CORE-1479 — состояние наградных реакций без зависимостей.
 *
 * Два канала, оба — обычный модуль, как `flashNotice`:
 *
 *   • снимок «кто сейчас и что у него с наградой» — его пишет AuthContext при
 *     каждой смене пользователя, а читает `useRewardReactions`. Хук не
 *     импортирует AuthContext: тот тянет за собой Sentry, телеметрию и
 *     десяток сервисов, и каждый модуль, где ставят реакцию (хабы комнаты,
 *     ряды реакций), получал бы их все — вместе со своими тестами;
 *
 *   • свежее состояние из ответа на сохранение профиля — его публикует
 *     `services/api`, которому лишнее тянуть тоже нельзя, а слушает
 *     AuthContext.
 */
import type { RewardReactionsState } from '../types/auth';

// ── Снимок для хука ─────────────────────────────────────────────────────────

export interface RewardReactionsSnapshot {
  signedIn: boolean;
  /** undefined — сервер о награде ещё не знает; null — состояния нет. */
  state: RewardReactionsState | null | undefined;
}

let snapshot: RewardReactionsSnapshot = { signedIn: false, state: undefined };
const snapshotListeners = new Set<() => void>();

export function setRewardReactionsSnapshot(next: RewardReactionsSnapshot): void {
  if (next.signedIn === snapshot.signedIn && next.state === snapshot.state) return;
  snapshot = next;
  snapshotListeners.forEach((listener) => listener());
}

export function getRewardReactionsSnapshot(): RewardReactionsSnapshot {
  return snapshot;
}

export function subscribeRewardReactionsSnapshot(listener: () => void): () => void {
  snapshotListeners.add(listener);
  return () => {
    snapshotListeners.delete(listener);
  };
}

// ── Свежее состояние после сохранения профиля ───────────────────────────────

export interface RewardReactionsUpdate {
  state: RewardReactionsState;
  /** Открылись именно этим сохранением — повод поздравить. */
  justUnlocked: boolean;
}

type Listener = (update: RewardReactionsUpdate) => void;
const listeners = new Set<Listener>();

export function publishRewardReactions(update: RewardReactionsUpdate): void {
  listeners.forEach((listener) => listener(update));
}

export function subscribeRewardReactions(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
